<template>
    <v-card class="ai-follow-panel" elevation="8" role="dialog" aria-label="Quaro AI chat">
        <div class="ai-follow-panel-head">
            <v-icon small color="primary" class="mr-2">mdi-robot-outline</v-icon>
            <span class="ai-follow-panel-title">Quaro AI</span>
            <v-spacer></v-spacer>
            <v-tooltip bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="d-flex align-center">
                        <v-switch v-model="follow" dense inset hide-details class="ai-follow-panel-switch mt-0 pt-0" aria-label="Show me where the data is" @change="onFollowChange"></v-switch>
                        <span class="ai-follow-panel-switch-label">Preview</span>
                    </div>
                </template>
                <span>
                    Preview on: while the AI answers, the cursor shows you where the data lives in Quaro. Clicking on the page stops the preview on purpose, so it never moves your screen
                    while you work. Preview off: the AI only answers here.
                </span>
            </v-tooltip>
            <v-btn x-small text color="primary" class="ml-2" @click="$emit('full')">Full chat</v-btn>
            <v-btn icon x-small aria-label="Hide chat" @click="$emit('close')"><v-icon small>mdi-chevron-up</v-icon></v-btn>
        </div>
        <v-divider></v-divider>

        <div ref="body" class="ai-follow-panel-body">
            <div v-if="!alive" class="ai-follow-panel-note">
                The chat is no longer kept in the background (Quaro frees memory after many page visits). Your messages are saved.
                <v-btn x-small outlined color="primary" class="mt-2" @click="$emit('full')">Open the full chat</v-btn>
            </div>
            <template v-else-if="exchange.length">
                <div v-for="(msg, index) in exchange" :key="index" class="ai-follow-msg" :class="msg.user === 'KI' ? 'ai-follow-msg--ai' : 'ai-follow-msg--me'">
                    <template v-if="msg.user === 'KI'">
                        <div class="ai-follow-msg-text" v-html="renderMarkdown(msg.text)"></div>

                        <!-- Wo steht das in Quaro? gleiche Wegbeschreibung wie unter "Sources" im grossen Chat -->
                        <div v-if="placesOf(msg).length" class="ai-follow-places">
                            <div class="ai-follow-places-title"><v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>Where to find it in Quaro</div>
                            <div v-for="place in placesOf(msg)" :key="place.key" class="ai-follow-place">
                                <span class="ai-follow-place-text" :title="place.text">{{ place.text }}</span>
                                <v-btn v-if="place.targets.length === 1" x-small text color="primary" :to="place.targets[0].route" @click="prepare(place.targets[0])">Open</v-btn>
                                <v-menu v-else offset-y max-height="260">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn x-small text color="primary" v-bind="attrs" v-on="on">Open<v-icon x-small right>mdi-menu-down</v-icon></v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item v-for="target in place.targets" :key="target.key" :to="target.route" @click="prepare(target)">
                                            <v-list-item-title>{{ target.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                        </div>

                        <!-- Freigabe (Loeschen, Veroeffentlichen): ohne Klick laeuft nichts -->
                        <div v-if="msg.approval" class="ai-follow-approval">
                            <div v-for="req in msg.approval.requests" :key="req.id" class="ai-follow-approval-item">
                                <div class="font-weight-medium">{{ req.label }}</div>
                                <div v-if="req.details" class="ai-follow-approval-details">{{ req.details }}</div>
                                <ul v-if="req.items && req.items.length" class="ai-follow-approval-list">
                                    <li v-for="(item, itemIndex) in req.items" :key="itemIndex">{{ item }}</li>
                                </ul>
                            </div>
                            <div v-if="approvalIsOpen(msg)" class="mt-2">
                                <v-btn small rounded depressed color="primary" class="mr-2" :disabled="loading" @click="answerApproval(msg, true)">
                                    <v-icon small left>mdi-check</v-icon>Confirm
                                </v-btn>
                                <v-btn small rounded text :disabled="loading" @click="answerApproval(msg, false)">Cancel</v-btn>
                            </div>
                            <div v-else class="ai-follow-approval-state">{{ approvalStateText(msg) }}</div>
                        </div>
                    </template>
                    <div v-else class="ai-follow-msg-text">{{ msg.text }}</div>
                </div>
            </template>
            <div v-else class="ai-follow-panel-note">Ask a question below. While the AI answers, you see where the data lives in Quaro.</div>

            <div v-if="loading" class="ai-follow-working">
                <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
                <span>{{ label || "Quaro AI is working" }}</span>
            </div>
        </div>

        <v-divider></v-divider>
        <div class="ai-follow-panel-input">
            <v-textarea
                v-model="input"
                solo
                flat
                dense
                hide-details
                auto-grow
                rows="1"
                no-resize
                background-color="grey lighten-4"
                placeholder="Ask the next question..."
                aria-label="Ask Quaro AI"
                :disabled="!alive"
                @keydown.enter.exact.prevent="send"></v-textarea>
            <v-btn v-if="!loading" icon color="primary" aria-label="Send" :disabled="!input.trim() || !alive" @click="send"><v-icon>mdi-send</v-icon></v-btn>
            <v-btn v-else icon color="red" aria-label="Stop" @click="$root.$emit('aiFollowCancel')"><v-icon>mdi-close-circle</v-icon></v-btn>
        </div>
    </v-card>
</template>

<script>
    import MarkdownIt from "markdown-it"
    import { describeSourcePlaces, addCreatedProjectNames, followWanted, setFollowWanted } from "./aiCursorMap"
    import { buildIdNameMap, collectOutputs } from "./helper/approvalNames"

    const markdown = new MarkdownIt()

    // Der Chat aus der oberen Leiste: zeigt nur an und ruft den im Hintergrund laufenden Chat (SEOKI.vue, KeepAlive) per Event auf -
    // es gibt keine zweite Chat-Logik. Senden, Abbrechen und Freigaben laufen also genau wie im grossen Chat.
    export default {
        name: "AiFollowPanel",
        props: { loading: Boolean, label: { type: String, default: "" } },
        data: () => ({ input: "", alive: false, follow: followWanted() }),
        computed: {
            chat() {
                const chats = this.$store.state.user_settings?.ki_chats?.chats || []
                return chats[this.$store.state.user_settings?.ki_chats?.active_chat ?? 0] || null
            },
            exchange() {
                const messages = (this.chat && this.chat.messages) || []
                let start = 0
                for (let i = messages.length - 1; i >= 0; i--) {
                    if (messages[i].user !== "KI") {
                        start = i
                        break
                    }
                }
                return messages.slice(start)
            },
            idNames() {
                const messages = (this.chat && this.chat.messages) || []
                const names = buildIdNameMap(collectOutputs(messages, []))
                messages.forEach((message) => addCreatedProjectNames(names, message && message.tables))
                return names
            }
        },
        watch: {
            exchange() {
                this.scrollDown()
            },
            loading() {
                this.scrollDown()
            }
        },
        methods: {
            renderMarkdown(text) {
                return markdown.render(text || "")
            },
            scrollDown() {
                this.$nextTick(() => {
                    const body = this.$refs.body
                    if (body) body.scrollTop = body.scrollHeight
                })
            },
            placesOf(msg) {
                const tables = Array.isArray(msg.tables) ? msg.tables : []
                const byName = new Map()
                tables.forEach((table) => {
                    const name = (table && table.name) || "unknown"
                    if (!byName.has(name)) byName.set(name, [])
                    byName.get(name).push(table)
                })
                const options = { isAdmin: !!this.$store.state.admin, projectName: (id) => this.idNames.get(id) || null }
                const seen = new Set()
                const places = []
                byName.forEach((group) => {
                    const described = describeSourcePlaces(group, options)
                    if (!described.targets.length || seen.has(described.place)) return
                    seen.add(described.place)
                    places.push({ key: described.place, text: described.place, targets: described.targets })
                })
                return places
            },
            prepare(target) {
                if (target && target.sendKeywords) this.$store.dispatch("SET_SEND_TO_KEYWORDS", target.sendKeywords)
            },
            approvalIsOpen(msg) {
                if (!msg || !msg.approval || msg.approval.state !== "pending") return false
                return Boolean(this.chat && this.chat.latest_resp_id && this.chat.latest_resp_id === msg.approval.resp_id)
            },
            approvalStateText(msg) {
                const state = msg.approval.state
                if (state === "approved") return "You confirmed this action."
                if (state === "declined") return "You cancelled this action - nothing was changed."
                if (state === "failed") return "Could not be completed - it may or may not have run."
                return "Expired - ask again if you still want to do this."
            },
            answerApproval(msg, approve) {
                this.$root.$emit("aiFollowApproval", { msg, approve })
            },
            send() {
                const text = this.input.trim()
                if (!text || this.loading || !this.alive) return
                this.$root.$emit("aiFollowSend", text)
                this.input = ""
            },
            onFollowChange(value) {
                setFollowWanted(Boolean(value))
            }
        },
        mounted() {
            // Lebt der Chat noch im Hintergrund? Er antwortet auf den Ping - sonst ist er aus dem KeepAlive verdraengt worden.
            this.$root.$emit("aiFollowPing", () => {
                this.alive = true
            })
            this.scrollDown()
        }
    }
</script>

<style scoped>
    .ai-follow-panel {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-height: min(70vh, 640px);
        border-radius: 12px;
        font-size: 13px;
    }
    .ai-follow-panel-head {
        display: flex;
        align-items: center;
        padding: 6px 8px 6px 12px;
    }
    .ai-follow-panel-title {
        font-weight: 600;
    }
    .ai-follow-panel-switch {
        margin: 0;
        flex: 0 0 auto;
    }
    .ai-follow-panel-switch-label {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
    }
    .ai-follow-panel-body {
        flex: 1 1 auto;
        min-height: 60px;
        overflow-y: auto;
        padding: 10px 12px;
    }
    .ai-follow-panel-note {
        color: rgba(0, 0, 0, 0.6);
    }
    .ai-follow-msg {
        margin-bottom: 10px;
    }
    .ai-follow-msg--me .ai-follow-msg-text {
        margin-left: auto;
        max-width: 85%;
        padding: 6px 10px;
        border-radius: 12px;
        background: rgba(0, 159, 137, 0.12);
    }
    .ai-follow-msg-text >>> table {
        display: block;
        max-width: 100%;
        overflow-x: auto;
        border-collapse: collapse;
        font-size: 12px;
    }
    .ai-follow-msg-text >>> th,
    .ai-follow-msg-text >>> td {
        padding: 3px 8px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        text-align: left;
        white-space: nowrap;
    }
    .ai-follow-msg-text >>> p {
        margin-bottom: 6px;
    }
    .ai-follow-places {
        margin-top: 6px;
        padding: 6px 8px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 8px;
    }
    .ai-follow-places-title {
        font-size: 12px;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.6);
    }
    .ai-follow-place {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        font-size: 12px;
    }
    .ai-follow-place-text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .ai-follow-approval {
        margin-top: 8px;
        padding: 8px 10px;
        border: 1px solid rgba(0, 159, 137, 0.4);
        border-radius: 8px;
    }
    .ai-follow-approval-details,
    .ai-follow-approval-state {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.65);
    }
    .ai-follow-approval-list {
        margin: 4px 0 0;
        padding-left: 18px;
        font-size: 12px;
    }
    .ai-follow-working {
        display: flex;
        align-items: center;
        color: rgba(0, 0, 0, 0.65);
    }
    .ai-follow-panel-input {
        display: flex;
        align-items: flex-end;
        gap: 4px;
        padding: 8px 8px 8px 12px;
    }
    .ai-follow-panel-input >>> textarea {
        max-height: 120px;
        overflow-y: auto;
    }
</style>
